import { Tooltip } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { generateBreadcrumbs } from "@/routers/utils";
import { useClinicInfo } from "@/stores";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import EnvironmentBadge from "../EnvironmentBadge";
import Breadcrumbs from "./Breadcrumbs";
import HeaderActions from "./HeaderActions";

export default function HeaderPage() {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const selectedLocation = useAdditionalInfo((state) => state.location);
  const { clinicName } = useClinicInfo();
  const locationLabel = selectedLocation?.label || clinicName || "N/A";
  const locationText = `Location: ${locationLabel}`;
  const locationRef = useRef<HTMLSpanElement | null>(null);
  const [isLocationTruncated, setIsLocationTruncated] = useState(false);

  const updateLocationTruncation = useCallback(() => {
    const element = locationRef.current;
    if (!element) {
      setIsLocationTruncated(false);
      return;
    }
    setIsLocationTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useEffect(() => {
    if (!locationLabel) {
      setIsLocationTruncated(false);
      return;
    }
    updateLocationTruncation();
  }, [updateLocationTruncation, locationLabel]);

  useEffect(() => {
    window.addEventListener("resize", updateLocationTruncation);
    return () => window.removeEventListener("resize", updateLocationTruncation);
  }, [updateLocationTruncation]);

  const getBreadcrumbItems = () => {
    const state = (routeLocation.state || {}) as { id?: string | number; name?: string };
    return generateBreadcrumbs(routeLocation.pathname, state);
  };

  return (
    <div className="h-[64px] w-full bg-white" style={{ boxShadow: "0px 2px 8px 0px #3D4D991A" }}>
      <div className="flex h-full">
        <div className="flex-1 border-r border-gray-200 flex items-center justify-between gap-2 px-6 py-3">
          <Breadcrumbs
            items={getBreadcrumbItems()}
            className="text-dark-blue-one !text-[12px] sm:!text-[14px] md:!text-[20px] font-Figtree"
          />
          <div className="flex items-center gap-3">
            <EnvironmentBadge size="small" />
            <Tooltip title={isLocationTruncated ? locationLabel : null}>
              <span ref={locationRef} className="max-w-[240px] truncate block">
                {locationText}
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="flex gap-2 px-6 py-3">
          <HeaderActions onAddPatient={() => navigate("/registration")} />
        </div>
      </div>
    </div>
  );
}
